import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} trigger
 * @property {HTMLButtonElement} closeButton
 */

class CompatibleProductsDrawer extends DialogComponent {
  requiredRefs = ['dialog', 'trigger', 'closeButton'];

  /** @type {HTMLElement | null} */
  #lastFocusedElement = null;

  #abortController = new AbortController();

  get #refs() {
    return /** @type {Refs} */ (this.refs);
  }

  connectedCallback() {
    super.connectedCallback();

    this.#syncState(false);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener('dialog:open', this.#handleOpen);
    this.addEventListener('dialog:close', this.#handleClose);

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  disconnectedCallback() {
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.removeEventListener('dialog:open', this.#handleOpen);
    this.removeEventListener('dialog:close', this.#handleClose);

    this.#abortController.abort();

    super.disconnectedCallback();
  }

  showDialog() {
    this.#lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    super.showDialog();
  }

  #handleOpen = () => {
    this.#syncState(true);

    requestAnimationFrame(() => {
      this.#refs.closeButton.focus();
    });
  };

  #handleClose = () => {
    this.#syncState(false);
    this.#lastFocusedElement?.focus();
  };

  /** @param {Event} event */
  #handleVariantUpdate = (event) => {
    const detail = this.#getVariantUpdateDetail(event);
    if (!detail?.data) return;

    const target = event.target;
    if (target instanceof HTMLElement) {
      const compatibleCard = target.closest('[data-compatible-product-card]');
      if (compatibleCard instanceof HTMLElement && compatibleCard.closest('compatible-products-drawer') === this) {
        this.#syncCompatibleCardFromVariantUpdate(compatibleCard, detail);
        event.stopPropagation();
        return;
      }
    }
  };

  /** @param {Event} event */
  #getVariantUpdateDetail(event) {
    if (!('detail' in event)) return null;

    const detail = event.detail;
    if (!detail || typeof detail !== 'object') return null;

    return /** @type {{ resource?: Record<string, unknown> | null, data?: { html?: ParentNode | null, productId?: string, newProduct?: { id?: string } | null } }} */ (detail);
  }

  /** @param {string | null | undefined} text */
  #parseJson(text) {
    if (!text) return null;

    try {
      return JSON.parse(text);
    } catch (error) {
      console.error('Failed to parse compatible products JSON', error);
      return null;
    }
  }

  /** @param {unknown} value */
  #toNumber(value, fallback = 0) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
  }

  /**
   * @param {HTMLElement} card
   * @param {{ resource?: Record<string, unknown> | null, data?: { html?: ParentNode | null, productId?: string, newProduct?: { id?: string } | null } }} detail
   */
  #syncCompatibleCardFromVariantUpdate(card, detail) {
    const variantId = String(detail.resource?.id ?? '');
    if (!variantId) return;

    const variantState = this.#getCompatibleCardVariantState(card, variantId) ?? this.#buildCompatibleCardVariantStateFallback(card, detail);
    if (!variantState) return;

    if (detail.data?.newProduct?.id) {
      card.dataset.productId = String(detail.data.newProduct.id);
    }

    card.dataset.variantId = variantId;
    card.dataset.productPrice = String(this.#toNumber(variantState.price, 0));
    card.dataset.productAvailable = variantState.available === true ? 'true' : 'false';
    card.dataset.productInventoryManaged = variantState.inventoryManaged === true ? 'true' : 'false';
    card.dataset.productInventoryPolicy = String(variantState.inventoryPolicy ?? '');
    card.dataset.productInventoryQuantity = String(this.#toNumber(variantState.inventoryQuantity, 0));

    const nextImageUrl = String(variantState.imageUrl ?? '');
    const nextImageAlt = String(variantState.imageAlt ?? card.dataset.productTitle ?? '');

    card.dataset.productImage = nextImageUrl;
    card.dataset.productImageAlt = nextImageAlt;

    this.#syncCompatibleCardImage(card, nextImageUrl, nextImageAlt);
    this.#syncCompatibleCardLink(card, detail);
    this.#syncCompatibleCardPrice(card, detail);
    this.#syncCompatibleCardAvailability(card, variantState.available === true);
  }

  /**
   * @param {HTMLElement} card
   * @param {string} variantId
   */
  #getCompatibleCardVariantState(card, variantId) {
    const variantScript = card.querySelector('[data-compatible-product-variants]');
    if (!(variantScript instanceof HTMLScriptElement)) return null;

    const variantStates = this.#parseJson(variantScript.textContent);
    if (!Array.isArray(variantStates)) return null;

    return variantStates.find((variantState) => String(variantState?.variantId ?? '') === variantId) ?? null;
  }

  /**
   * @param {HTMLElement} card
   * @param {{ resource?: Record<string, unknown> | null }} detail
   */
  #buildCompatibleCardVariantStateFallback(card, detail) {
    const resource = detail.resource;
    if (!resource) return null;

    const resourceData = /** @type {{
     *   id?: string | number,
     *   price?: unknown,
     *   available?: unknown,
     *   inventory_management?: unknown,
     *   inventory_policy?: unknown,
     *   inventory_quantity?: unknown,
     *   quantity_rule?: { max?: unknown } | null,
     *   image?: { src?: string, url?: string, alt?: string } | null,
     *   featured_image?: { src?: string, url?: string, alt?: string } | null,
     *   featured_media?: { preview_image?: { src?: string, url?: string, alt?: string } | null } | null,
     * }} */ (resource);

    const featuredImage = resourceData.featured_image ?? resourceData.featured_media?.preview_image ?? resourceData.image ?? null;
    const imageUrl = typeof featuredImage?.src === 'string'
      ? featuredImage.src
      : typeof featuredImage?.url === 'string'
      ? featuredImage.url
      : card.dataset.productImage ?? '';
    const imageAlt = typeof featuredImage?.alt === 'string'
      ? featuredImage.alt
      : card.dataset.productImageAlt ?? card.dataset.productTitle ?? '';

    return {
      variantId: String(resourceData.id ?? ''),
      price: this.#toNumber(resourceData.price, 0),
      available: Boolean(resourceData.available),
      inventoryManaged: String(resourceData.inventory_management ?? '') === 'shopify',
      inventoryPolicy: String(resourceData.inventory_policy ?? ''),
      inventoryQuantity: this.#toNumber(resourceData.inventory_quantity, 0),
      imageUrl,
      imageAlt,
    };
  }

  /**
   * @param {HTMLElement} card
   * @param {string} imageUrl
   * @param {string} imageAlt
   */
  #syncCompatibleCardImage(card, imageUrl, imageAlt) {
    let image = card.querySelector('[data-compatible-product-image]');
    if (!(image instanceof HTMLImageElement)) {
      image = card.querySelector('.jj-compatible-products-card-image');
    }

    const placeholder = card.querySelector('[data-compatible-product-placeholder]');
    const media = card.querySelector('.jj-compatible-products-card-media');

    if (!(image instanceof HTMLImageElement) && imageUrl && media instanceof HTMLElement) {
      const createdImage = document.createElement('img');
      createdImage.className = 'jj-compatible-products-card-image';
      createdImage.dataset.compatibleProductImage = '';
      createdImage.loading = 'lazy';
      createdImage.decoding = 'async';
      media.prepend(createdImage);
      image = createdImage;
    }

    if (image instanceof HTMLImageElement) {
      if (imageUrl) {
        image.src = imageUrl;
        if (image.srcset) {
          image.srcset = '';
        }
        image.alt = imageAlt;
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    if (placeholder instanceof HTMLElement) {
      placeholder.hidden = Boolean(imageUrl);
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {{ data?: { html?: ParentNode | null } }} detail
   */
  #syncCompatibleCardLink(card, detail) {
    const variantPickerLink = detail.data?.html?.querySelector('a[ref="productCardLink"]');
    if (!(variantPickerLink instanceof HTMLAnchorElement)) return;

    for (const link of card.querySelectorAll('[data-compatible-product-link]')) {
      if (link instanceof HTMLAnchorElement) {
        link.href = variantPickerLink.href;
      }
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {{ data?: { html?: ParentNode | null } }} detail
   */
  #syncCompatibleCardPrice(card, detail) {
    const priceTarget = card.querySelector('[data-compatible-product-price]');
    if (!(priceTarget instanceof HTMLElement)) return;

    const nextPrice = detail.data?.html?.querySelector('product-price');
    if (!(nextPrice instanceof HTMLElement)) return;

    priceTarget.innerHTML = nextPrice.innerHTML;
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} isAvailable
   */
  #syncCompatibleCardAvailability(card, isAvailable) {
    card.toggleAttribute('aria-disabled', !isAvailable);
  }

  /** @param {boolean} isOpen */
  #syncState(isOpen) {
    this.#refs.trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    this.#refs.dialog.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
  }
}

if (!customElements.get('compatible-products-drawer')) {
  customElements.define('compatible-products-drawer', CompatibleProductsDrawer);
}